import posthog from "posthog-js";
import { DEMO_EVENTS, EventName } from "./events";
import { isDemo } from "../config/demo";

/**
 * PostHog capture wrapper for the web client.
 *
 * Imports the `posthog-js` singleton directly rather than going through
 * `contexts/PostHogContext` — that path is a circular import via `AuthContext`,
 * and it has bitten us before.
 *
 * Never throws: analytics must not break the UI. Silently no-ops when PostHog
 * failed to initialize (e.g. `REACT_APP_POSTHOG_API_KEY` unset in a local build).
 *
 * `platform` / `surface` / `app_version` are registered as super properties by
 * `PostHogContext` at init time, so call sites never pass them.
 */
export const capture = (
  event: EventName,
  properties?: Record<string, unknown>
): void => {
  if (!posthog.__loaded) return;
  // The Live sample sends its three events and nothing else (PRD 117, D10).
  if (isDemo() && !DEMO_EVENTS.includes(event)) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never break the UI.
  }
};

/**
 * Tag every later event with the sample being explored. A super property, not
 * identify(): all visitors of a persona share one user, and identifying it
 * would merge every one of them into a single person.
 */
export const registerDemoPersona = (persona: string): void => {
  if (!posthog.__loaded) return;
  try {
    posthog.register({ demo_persona: persona });
  } catch {
    // Analytics must never break the UI.
  }
};

/** Current PostHog distinct id, or null if PostHog is not initialized. */
export const getDistinctId = (): string | null => {
  if (!posthog.__loaded) return null;
  try {
    return posthog.get_distinct_id() ?? null;
  } catch {
    return null;
  }
};
